import _ from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Alert, Button, Card } from 'react-bootstrap';
import Select from 'react-select';
import momentTZ from 'moment-timezone';

import Api from '../../services/ApiService';
import PageService from '../../services/PageService';
import Socket from '../../services/SocketService';
import EmployeesModeConductor from './components/EmployeesModeConductor';
import LoadingPage from '../LoadingPage';
import MechanicsModeConductor from './components/MechanicsModeConductor';
import UserInput from './components/UserInput';
import { modal } from '../modal/ModalEmitter';

import '../../styles/timeclock.scss';

// Please note the Timeclock was my first foray into React-land in late '16 ¯\_(ツ)_/¯ - CK

const AUTO_RESET_SECS = 180; // reset screen after inactivity every 3 mins
const CLOCKDRIFT_CHECK_SECS = 300; // sync to server time every 5 mins

const itemName = 'TIMECLOCK';

class TimeClock extends Component {
  static propTypes = {
    clockType: PropTypes.oneOf(['employees', 'mechanics']),
    user: PropTypes.object,
  };

  state = {
    lastRO: null,
    lastRODetails: '',
    lastPunch: null,
    causeText: '',
    correctionText: '',
    modeActive: null,
    userActive: null,
    password: '',
  };

  activeStory = false; // used by mechanics clock
  modes = null;
  serverTimestamp = null; // ms since epoch
  timezoneAbbr = '';
  users = null;
  modeLookup = {};
  hideOpComplete = false;
  hideRemedyCode = false;
  hideMileage = false;
  hidePassword = false;

  autoResetScreenTimeout = null;
  inactivityTimeout = null;

  clockDriftSyncInterval = null;
  clockTickInterval = null;

  constructor(props) {
    super(props);
    this.state = { ...this.state, password: this.props.user.ppcPassword || '' };
  }

  componentDidMount() {
    // get time clock modes available (varies per client) & user list
    this.getClockSetup();
  }

  componentWillUnmount() {
    clearTimeout(this.autoResetScreenTimeout);
    clearTimeout(this.inactivityTimeout);
    clearInterval(this.clockDriftSyncInterval);
    clearInterval(this.clockTickInterval);
    this.removeLock();
  }

  displayTime() {
    return `${momentTZ(this.serverTimestamp).format('hh:mm:ss A')} ${this.timezoneAbbr}`;
  }

  getLockItem = id => {
    return `${id}-${momentTZ(this.serverTimestamp).format('MM-DD-YYYY')}`;
  };

  getLockId = id => {
    return `${this.props.user.branch.mvAccount}-${itemName}-${this.getLockItem(id)}.lock`;
  };

  getLockStatus = lockItem => {
    return new Promise(resolve => {
      PageService.getLock(itemName, lockItem).then(response => {
        const lockedBy = response.data.user;
        const isMe = lockedBy ? lockedBy.username === this.props.user.username : false;
        const isLocked = lockedBy !== null && !isMe;
        resolve({ isLocked, lockedBy });
      });
    });
  };

  showLockedDialog = (lockedBy, lockItem) => {
    modal.open('pageLocked', {
      callback: () => {
        PageService.unlockPage(itemName, this.getLockItem(lockItem));
        PageService.lockPage(itemName, this.getLockItem(lockItem));
        this.onUserChange(lockItem);
      },
      onClose: () => {
        this.reset();
      },
      lockedBy,
    });
  };

  removeLock() {
    const lockItem = this.state.userActive;
    if (lockItem) {
      Socket.global.off(this.getLockId(lockItem));
      PageService.unlockPage(itemName, this.getLockItem(lockItem));
    }
  }

  getClockSetup() {
    if (!_.includes(['employees', 'mechanics'], this.props.clockType)) {
      console.error('Invalid clocktype specified: must be "employees" or "mechanics".');
      return;
    }

    Api.get(`timeclock/setup/${this.props.clockType}`).then(res => {
      this.modes = [];
      res.data.modes.forEach(mode => {
        this.modes.push({
          value: mode[0],
          label: `${mode[0]} : ${mode[1]}`,
        });

        this.modeLookup[mode[0]] = { label: mode[1], cc: mode[2] === 'Y' };
      });

      this.users = {};
      res.data.users.forEach(user => {
        // 0: ID, 1: display name, (2: current mode - ATP1 only for now)
        const id = user[0].toUpperCase();
        this.users[id] = user[1];
      });

      // attempt to guess workstation's TZ abbreviation
      this.timezoneAbbr = momentTZ.tz(momentTZ.tz.guess()).zoneAbbr();
      this.timezoneAbbr = this.timezoneAbbr === 'UTC' ? '' : this.timezoneAbbr; // couldn't guess TZ

      this.hideOpComplete = res.data.hideOpComplete === 'Y';
      this.hideRemedyCode = res.data.hideRemedyCode === 'N';
      this.hideMileage = res.data.hideMileage === 'Y';
      this.hidePassword = res.data.hidePassword === 'Y';
      this.activeStory = res.data.showStory === 'X' && this.props.clockType === 'mechanics';

      // sync serverTimestamp here + create clock drift check interval
      this.syncServerTime().then(() => {
        this.clockDriftSyncInterval = setInterval(() => {
          this.syncServerTime();
        }, CLOCKDRIFT_CHECK_SECS * 1000);

        // keep the clock ticking by direct DOM reference - avoids unnecessary render()s
        this.clockTickInterval = setInterval(() => {
          if (this.isReady()) {
            this.serverTimestamp += 1000;
            const timeEl = document.getElementById('tc-timestamp-display');
            if (timeEl) {
              timeEl.innerHTML = this.displayTime();
            }
          }
        }, 1000);

        this.forceUpdate();
      });
    });
  }

  // Pop an inactivity warning after AUTO_RESET_SECS
  inactivityTimeoutRestart = () => {
    // User has stopped any final warning / pending auto-reset
    // by key/click.  If no pending auto-reset exists, this has
    // nil effect.
    clearTimeout(this.autoResetScreenTimeout);
    const element = document.getElementById('alertWarning');
    if (element) {
      element.style.display = 'none';
    }

    // Reset inactivity timeout on every key/click (keys are throttled)
    clearTimeout(this.inactivityTimeout);

    // The actual inactivity timeout culminates in a final warning
    // displayed 10s before auto reset-screen.  User can cancel by click/key.
    this.inactivityTimeout = setTimeout(() => {
      const element = document.getElementById('alertWarning');

      if (element) {
        // After inactivity timeout reached, show final warning
        element.style.display = 'block';
      }

      // Give the user 10 more seconds to clear this
      this.autoResetScreenTimeout = setTimeout(() => {
        this.reset();
      }, 10 * 1000);
    }, AUTO_RESET_SECS * 1000);
  };

  isReady() {
    return this.serverTimestamp && this.modes && this.users;
  }

  onModeChange = option => {
    const modeActive = option ? option.value : null;
    this.setState({
      modeActive,
    });
  };

  onUserChange = async id => {
    if (id) {
      const status = await this.getLockStatus(this.getLockItem(id));

      if (status.isLocked) {
        this.showLockedDialog(status.lockedBy, id);
        return;
      }

      PageService.unlockPage(itemName, this.getLockItem(this.state.userActive));
      PageService.lockPage(itemName, this.getLockItem(id));
    }

    Socket.global.on(this.getLockId(id), (lockedBy = {}) => {
      if (this.state.userActive && this.props.user.id !== lockedBy.id) {
        this.reset();
        modal.info({
          message: `Force unlocked was issued by ${lockedBy.firstName} ${lockedBy.lastName}.`,
          title: 'Notice',
          size: 'sm',
        });
      }
    });

    // do this immediately for snappy UI
    this.setState({
      lastRO: null,
      userActive: id,
    });

    // do this async in case system is slow / pull latest info
    if (this.props.clockType === 'mechanics' && id !== null) {
      Api.post('timeclock/lastro', { id }).then(res => {
        this.setState({
          lastRO: res.data.lastRo ? `${res.data.lastRo} - ${res.data.lastRoCustomer}` : null,
          lastRODetails: res.data.lastRoDetails,
          lastPunch: res.data.lastPunch,
          causeText: res.data.causeText,
          correctionText: res.data.correctionText,
        });
      });
    }
  };

  reset = e => {
    this.removeLock();
    this.setState({
      lastRO: null,
      modeActive: null,
      userActive: null,
      password: '',
    });

    this.userInputRef.reset().focus();

    // Clear any pending inactivity timeouts
    document.getElementById('alertWarning').style.display = 'none';
    clearTimeout(this.autoResetScreenTimeout);
    clearTimeout(this.inactivityTimeout);

    // Prevents propagation of Reset Screen button clicks to inactivityTimeoutRestart,
    // which would reset the inactivity timer.  That is unnecessary until
    // we've detected another user using the screen.  reset() is not always
    // called from a click event, so check first.
    if (e) {
      e.stopPropagation();
    }
  };

  // UTC epoch, clients convert to local timezone
  syncServerTime() {
    return new Promise(resolve => {
      Api.get('timeclock/now').then(res => {
        this.serverTimestamp = res.data;
        resolve();
      });
    });
  }

  render() {
    if (!this.isReady()) {
      return <LoadingPage />;
    }

    let clockTypeHeader = '';
    let defaultValue = '';
    switch (this.props.clockType) {
      case 'employees':
        clockTypeHeader = 'Employee Time';
        defaultValue = this.props.user.employeeId || '';
        break;
      case 'mechanics':
        clockTypeHeader = 'Mobile Tech Time';
        defaultValue = this.props.user.technicianId || '';
        break;
      default:
        clockTypeHeader = 'Unsupported clocktype.';
    }

    const currentModeOption = this.modeLookup[this.state.modeActive]
      ? { label: this.modeLookup[this.state.modeActive].label, value: this.state.modeActive }
      : null;

    return (
      <div
        className="tc-module"
        onClick={this.inactivityTimeoutRestart}
        onKeyDown={_.throttle(this.inactivityTimeoutRestart, 300)}
      >
        <Card className="primary mb-3">
          <Card.Header>
            <Card.Title>{clockTypeHeader}</Card.Title>
          </Card.Header>
          <Card.Body>
            <div className="tc-time-header">
              <div className="tc-time-header-date">
                <i className="zmdi zmdi-calendar" /> {momentTZ(this.serverTimestamp).format('MMM D, YYYY')}
              </div>
              <div>
                <i className="zmdi zmdi-time" /> <span id="tc-timestamp-display">{this.displayTime()}</span>
              </div>
            </div>
            <div className="tc-mode-control">
              <UserInput
                advanceField={() => {
                  if (this.props.clockType === 'employees') {
                    this.passwordField.focus();
                  } else {
                    this.selectModeRef.focus();
                  }
                }}
                autofocus
                clockType={this.props.clockType}
                onChange={this.onUserChange}
                ref={ref => {
                  this.userInputRef = ref;
                }}
                users={this.users}
                defaultValue={defaultValue}
              />
              {!this.hidePassword && (
                <input
                  className="tc-password-control"
                  placeholder="Password"
                  type="password"
                  ref={ref => {
                    this.passwordField = ref;
                  }}
                  onChange={e => this.setState({ password: e.target.value })}
                  onKeyPress={evt => {
                    if (evt.key === 'Enter') {
                      this.passwordField.blur();
                      this.selectModeRef.focus();
                    }
                  }}
                  value={this.state.password}
                />
              )}
              <Select
                className="mode-select"
                matchPos="start"
                onChange={this.onModeChange}
                options={this.modes}
                placeholder="Select Mode"
                ref={ref => {
                  this.selectModeRef = ref;
                }}
                value={currentModeOption}
              />
            </div>
            {this.state.lastRO && (
              <div style={{ fontSize: '16px' }}>
                Last RO: {this.state.lastRO}
                {this.state.lastRODetails && (
                  <pre style={{ maxHeight: '200px', overflowY: 'scroll' }}>{this.state.lastRODetails}</pre>
                )}
              </div>
            )}
            <Button variant="warning" className="float-right" onClick={this.reset} tabIndex="-1">
              Reset Screen
            </Button>
            <div className="clearfix mb-3" />
            <Alert variant="danger" id="alertWarning" style={{ display: 'none' }}>
              <strong>Screen resetting in 10 seconds due to inactivity. Click or Key to cancel.</strong>
            </Alert>
          </Card.Body>
        </Card>

        {/* ATP1 */}
        {this.props.clockType === 'employees' && this.state.modeActive && this.state.userActive && (
          <EmployeesModeConductor
            mode={this.state.modeActive}
            reset={this.reset}
            userActive={this.state.userActive}
            password={this.state.password}
          />
        )}

        {/* STP2 */}
        {this.props.clockType === 'mechanics' && this.state.modeActive && this.state.userActive && (
          <MechanicsModeConductor
            activeStory={this.activeStory}
            hideMileage={this.hideMileage}
            hideOpComplete={this.hideOpComplete}
            hideRemedyCode={this.hideRemedyCode}
            lastPunch={this.state.lastPunch}
            causeText={this.state.causeText}
            correctionText={this.state.correctionText}
            mode={this.state.modeActive}
            modes={this.modeLookup}
            password={this.state.password}
            reset={this.reset}
            userActive={this.state.userActive}
          />
        )}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    user: state.app.user,
  };
}

export default connect(mapStateToProps)(TimeClock);
